import type { Language } from '@eformulieren/engine'
import { AdviserPanel, InterestDeclaration, LanguageSwitch, adviceAnchorTitle, followUpNews, formatDay, targetOf } from '@eformulieren/renderer'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type ReadAdviceFull, adviceViews, askerName } from './advice.js'
import { adviserStep, endText, judgedEndText } from './adviserStep.js'
import { type AdviserMode, FormPage } from './FormPage.js'
import type { FormContext } from './formContext.js'
import { headersFor } from './podRequests.js'
import { ProductBar } from './ProductBar.js'
import { LogoutButton } from './LogoutButton.js'
import { createShareClient } from './shareClient.js'

/**
 * De adviseurpagina (R-184, R-184c, R-184d, R-184g, R-184h, ontwerp 7.4a, mockup 05 van advies), op
 * `/<instantie>/advies` (`adviserRoute.ts`). Aannemen van een adviesvraag op de uitnodigingspagina gaat
 * hierheen; `main.tsx` stelt eerst vast wie er kijkt (`/whoami`, met inloggen als dat nodig is).
 *
 * **Eerst de belangenverklaring** (besluit van de gebruiker van 30 oktober 2026). Vóór de verklaring
 * ziet de adviseur de vraag, het anker, en in de control de titel, de aanvrager en de mede-aanvragers
 * (`partijen` uit `GET /advies`), en verder niets: de pagina vraagt geen projectie op en opent geen
 * verbinding. Het anker staat er als het label dat de opslag uit de definitie zette, want de namen
 * uit de projectie zijn er dan nog niet.
 *
 * **Geen belang**: `GET /advies` opnieuw (de partijen staan niet in het antwoord op de handeling), en
 * dan het formulier zoals de pod het voor deze adviseur projecteert: alleen de gedeelde secties, alleen
 * lezen, geen titel van een sectie die niet is gedeeld (R-38, R-68), met het anker vast gemarkeerd en
 * rechts het paneel met de vraag, de termijn, het gesprek en één antwoordvak (`FormPage` met
 * `adviseur`). **Belang**: het verzoek eindigt, en de pagina zegt dat eerlijk; stelde NWO het belang
 * vast (R-184k), dan zegt de pagina dat, met de toelichting van NWO. Sprak NWO een gemeld belang tegen,
 * dan loopt het verzoek weer en staat het oordeel van NWO in het paneel naast het formulier. **Afgerond, ingetrokken,
 * vervallen, of de beurt sloot**: een pagina dat de toegang is beëindigd en waarom; ook als dat gebeurt
 * terwijl het formulier openstaat (de pod sluit de verbinding, `onEinde`).
 *
 * Een vervolgvraag komt binnen door elke 30 seconden opnieuw te lezen, en wordt gemeld in de live regio
 * van het formulier.
 *
 * Een adviseur buiten NWO ziet elke medewerker als "NWO": de pod levert het merkteken en nooit een
 * sleutel (R-66a).
 */

export interface AdviserPageProps {
  readonly taal: Language
  readonly onLanguage: (language: Language) => void
  readonly context: FormContext
}

const T = {
  titel: { nl: 'Adviesvraag', en: 'Request for advice' },
  laden: { nl: 'De adviesvraag wordt geladen.', en: 'Loading the request for advice.' },
  geen: {
    nl: 'Er staat hier geen adviesvraag voor u. Klopt dat niet, open dan de link uit de e-mail opnieuw en log in met het adres waarop u de uitnodiging kreeg.',
    en: 'There is no request for advice for you here. If that is not right, open the link from the email again and sign in with the address the invitation was sent to.',
  },
  vanWie: { nl: '{w} vraagt uw advies.', en: '{w} asks for your advice.' },
  termijn: { nl: 'Antwoord graag voor {d}.', en: 'Answer requested by {d}.' },
  vraag: { nl: 'De vraag', en: 'The question' },
  eerst: {
    nl: 'Voordat u iets van het formulier ziet, verklaart u of u een belang heeft.',
    en: 'Before you see anything of the form, you declare whether you have an interest.',
  },
  knop: { nl: 'Advies', en: 'Advice' },
} as const

function logChain(chain: Readonly<Record<string, unknown>>): void {
  console.error('eformulieren: adviesvraag niet gelukt', chain)
}

export function AdviserPage({ taal: language, onLanguage, context }: AdviserPageProps): ReactNode {
  const instantieId = context.instantieId
  const headerKey = JSON.stringify(headersFor(context))
  const client = useMemo(
    () =>
      createShareClient({
        instantieId,
        headers: JSON.parse(headerKey) as Readonly<Record<string, string>>,
        taal: language,
        sectionName: () => undefined,
        fetch: (input, init) => globalThis.fetch(input, init),
        onSessionExpired: () => window.location.reload(),
        log: logChain,
      }),
    [instantieId, headerKey, language],
  )
  const [read, setRead] = useState<ReadAdviceFull | undefined | 'laden'>('laden')
  const load = useCallback(async () => {
    setRead(await client.adviceFull())
  }, [client])
  useEffect(() => {
    void load()
  }, [load])

  // Na de verklaring en na het einde: de focus op de kop van wat er dan staat.
  const heading = useRef<HTMLHeadingElement>(null)
  const [moved, setMoved] = useState(0)
  useEffect(() => {
    if (moved > 0) heading.current?.focus()
  }, [moved, read])

  const step = read === 'laden' ? undefined : adviserStep(read)
  const views = useMemo(
    () =>
      step !== undefined && (step.soort === 'lezen' || step.soort === 'verklaren')
        ? adviceViews([step.verzoek], {
            ik: context.persoon,
            taal: language,
            sectionName: () => undefined,
            fieldName: () => undefined,
            formName: step.verzoek.anker.label,
            emailOfInvitation: () => undefined,
            labels: {},
          })
        : [],
    [step, context.persoon, language],
  )
  const view = views[0]
  const request = step !== undefined && (step.soort === 'lezen' || step.soort === 'verklaren') ? step.verzoek : undefined
  const asker = request === undefined ? '' : askerName(request.vrager, request.vragerRol, language)
  const requestId = request?.id

  // Een vervolgvraag komt binnen door opnieuw te lezen: elke 30 seconden zolang het tabblad zichtbaar
  // is en het formulier openstaat. Er is geen kanaal dat de pagina iets toestuurt.
  const reading = step?.soort === 'lezen'
  useEffect(() => {
    if (!reading) return
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load()
    }, 30_000)
    return () => clearInterval(timer)
  }, [reading, load])
  const known = useRef<ReadonlySet<string> | undefined>(undefined)
  const [news, setNews] = useState('')
  useEffect(() => {
    if (view === undefined) return
    const before = known.current
    known.current = new Set(view.reacties.map((reaction) => reaction.id))
    if (before === undefined) return
    const said = followUpNews(before, view, asker, language)
    if (said !== undefined) setNews(said)
  }, [view, asker, language])

  const answer = useCallback(
    async (text: string) => {
      if (requestId === undefined) return { soort: 'gelukt' as const }
      const outcome = await client.react(requestId, text)
      await load()
      return outcome
    },
    [client, requestId, load],
  )
  const onEnd = useCallback(() => {
    void load().then(() => setMoved((count) => count + 1))
  }, [load])
  const adviser = useMemo<AdviserMode | undefined>(
    () =>
      view === undefined
        ? undefined
        : {
            anker: targetOf(view.anker),
            knop: T.knop[language],
            paneel: (panelContext) => <AdviserPanel taal={language} verzoek={view} vrager={asker} onAnker={panelContext.springNaar} onAntwoord={answer} focusBijOpenen={moved > 0} meldtZelf={false} />,
            onEinde: onEnd,
            melding: news,
          },
    [view, language, asker, answer, onEnd, moved, news],
  )

  if (step?.soort === 'lezen' && adviser !== undefined) {
    return <FormPage context={context} taal={language} onLanguage={onLanguage} mode="nalezen" adviseur={adviser} />
  }

  // Uitloggen ook vóór het formulier: de adviseur staat hier bij de verklaring of de melding (R-154).
  const bar = (
    <ProductBar
      taal={language}
      actions={
        <>
          <LanguageSwitch taal={language} onLanguage={onLanguage} compact />
          <LogoutButton taal={language} persoon={context.persoon} />
        </>
      }
    />
  )

  if (step === undefined) {
    return (
      <>
        {bar}
        <p className="pod-melding" role="status" data-proef="adviseur-laden">
          {T.laden[language]}
        </p>
      </>
    )
  }

  if (step.soort === 'geen' || step.soort === 'belang' || step.soort === 'beeindigd') {
    const text =
      step.soort === 'geen'
        ? { titel: T.titel[language], uitleg: T.geen[language], wat: '' }
        : step.soort === 'belang' && step.door === 'nwo'
          ? judgedEndText(step.toelichting, language)
          : endText(step.soort === 'belang' ? 'belang-gemeld' : step.reden, language)
    return (
      <>
        {bar}
        <section className="pod-geentoegang" lang={language} aria-labelledby="ef-advies-einde" data-proef={`adviseur-${step.soort}`}>
          <h1 id="ef-advies-einde" ref={heading} tabIndex={-1} className="pod-geentoegang__titel">
            {text.titel}
          </h1>
          <p className="pod-geentoegang__uitleg">{text.uitleg}</p>
          {text.wat === '' ? null : <p>{text.wat}</p>}
        </section>
      </>
    )
  }

  // De verklaring: de vraag en het anker boven de control, en verder niets van het formulier.
  const question = request?.reacties[0]?.tekst
  return (
    <>
      {bar}
      <section className="ef-uitnodiging" lang={language} aria-labelledby="ef-advies-titel" data-proef="adviseur-verklaren">
        <h1 id="ef-advies-titel" ref={heading} tabIndex={-1} className="ef-uitnodiging__titel">
          {T.titel[language]}
        </h1>
        <p className="ef-uitnodiging__tekst">{T.vanWie[language].replace('{w}', capital(asker))}</p>
        <section className="ef-adviesvraag" aria-labelledby="ef-advies-vraag">
          <h2 id="ef-advies-vraag" className="ef-sr-only">
            {T.vraag[language]}
          </h2>
          {question === undefined ? null : <p className="ef-adviesvraag__tekst">{question}</p>}
          {view === undefined ? null : <p className="ef-adviesvraag__wie">{adviceAnchorTitle(view.anker, language)}</p>}
          {request?.termijn === null || request === undefined ? null : (
            <p className="ef-adviesvraag__wie">{T.termijn[language].replace('{d}', formatDay(request.termijn, language))}</p>
          )}
        </section>
        <p className="ef-uitnodiging__tekst">{T.eerst[language]}</p>
        <InterestDeclaration
          taal={language}
          partijen={request?.partijen ?? null}
          kopNiveau={2}
          onDeclare={async (declaration) => {
            if (requestId === undefined) return
            await client.declare(requestId, declaration)
            await load()
            setMoved((count) => count + 1)
          }}
        />
      </section>
    </>
  )
}

function capital(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}
